// Stok hareketlerinden rapor özetleri üretir.
//
// Hareketler tek bir koleksiyonda (StockMovements) üç türde tutulur:
//   - type: 'assignment'   → depodan çalışana verilen miktar (Stok Yönetimi)
//   - type: 'visit_usage'  → çalışanın vizitte tükettiği miktar (Ziyaret Modalı)
//   - type: 'visit_return' → vizitten iade (miktarı NEGATİF kaydedilir)
//
// İade bir dağıtım değil, kullanımın geri alınmasıdır; bu yüzden 'used'
// toplamına eklenir ve negatif miktarı sayesinde tüketimi azaltır.
//
// Buradan üç soru cevaplanır:
//   "kime ne kadar verildi", "ne kadar kullanıldı", "elinde/depoda ne kadar kaldı".

export const MOVEMENT_ASSIGNMENT = 'assignment';
export const MOVEMENT_VISIT_USAGE = 'visit_usage';
export const MOVEMENT_VISIT_RETURN = 'visit_return';

/** Tüketim tarafına yazılan hareketler (iade dahil: negatif miktarla düşer). */
const isConsumptionMovement = (movement) =>
  movement?.type === MOVEMENT_VISIT_USAGE || movement?.type === MOVEMENT_VISIT_RETURN;

const num = (value) => {
  const parsed = parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const timeOf = (value) => {
  if (!value) return 0;
  const date = value.toDate ? value.toDate() : new Date(value);
  return Number.isNaN(date.getTime()) ? 0 : date.getTime();
};

/** Oran (0-1). Verilen yoksa 0 döner. */
const ratio = (used, assigned) => (assigned > 0 ? used / assigned : 0);

/** Sarf malzemesi mi? Müşteri/proje bağlantısı yalnızca sarf için anlamlı. */
export const isConsumable = (stock) => stock?.['Kategori'] === 'sarf';

/** Stok kartındaki müşteri/proje etiketleri (boşsa '-'). */
export const stockCustomerName = (stock) => stock?.customerName || '';
export const stockProjectName = (stock) => stock?.projectName || '';

/**
 * Çalışan bazlı özet.
 *
 * assigned  : kendisine verilen toplam
 * used      : vizitlerde tükettiği toplam
 * onHand    : elinde kalan (verilen - kullanılan; eksiye düşmez)
 * usageRate : kullanım oranı (used / assigned)
 * visitCount: tüketim yaptığı benzersiz vizit sayısı
 * perVisit  : vizit başına ortalama tüketim — "dikkatli kullanım" göstergesi
 */
export const summarizeByEmployee = (movements = []) => {
  const map = new Map();

  const ensure = (id, name) => {
    const key = id || 'unknown';
    if (!map.has(key)) {
      map.set(key, {
        key,
        employeeId: id || '',
        employeeName: name || 'Bilinmeyen çalışan',
        assigned: 0,
        used: 0,
        visitIds: new Set(),
        lastActivity: 0,
        stockNames: new Set(),
      });
    }
    const row = map.get(key);
    if (name && row.employeeName === 'Bilinmeyen çalışan') row.employeeName = name;
    return row;
  };

  movements.forEach((movement) => {
    const row = ensure(movement.employeeId, movement.employeeName);
    const quantity = num(movement.quantity);

    if (isConsumptionMovement(movement)) {
      row.used += quantity;
      // Vizit sayısı yalnızca gerçek kullanımdan sayılır; iade yeni bir vizit
      // anlamına gelmez.
      if (movement.type === MOVEMENT_VISIT_USAGE && movement.visitId) {
        row.visitIds.add(movement.visitId);
      }
    } else {
      row.assigned += quantity;
    }

    if (movement.stockName) row.stockNames.add(movement.stockName);
    row.lastActivity = Math.max(row.lastActivity, timeOf(movement.date));
  });

  return [...map.values()].map((row) => {
    const visitCount = row.visitIds.size;
    return {
      key: row.key,
      employeeId: row.employeeId,
      employeeName: row.employeeName,
      assigned: row.assigned,
      used: row.used,
      onHand: Math.max(0, row.assigned - row.used),
      usageRate: ratio(row.used, row.assigned),
      visitCount,
      perVisit: visitCount > 0 ? row.used / visitCount : 0,
      stockCount: row.stockNames.size,
      lastActivity: row.lastActivity || null,
    };
  });
};

/**
 * Müşteri / proje bazlı özet.
 *
 * Hareketler stok kartındaki müşteri-proje bilgisini taşır; taşımayan eski
 * kayıtlar için stok listesinden tamamlanır. Depoda kalan miktar hareketlerden
 * değil, stok kartındaki 'Kalan' alanından gelir.
 *
 * @param groupBy 'customer' | 'project'
 */
export const summarizeByScope = (movements = [], stocks = [], groupBy = 'customer') => {
  const stockById = new Map(stocks.map((stock) => [stock.id, stock]));
  const labelOf = (source) =>
    (groupBy === 'customer' ? source?.customerName : source?.projectName) || '';

  const map = new Map();
  // Müşterisi/projesi girilmemiş sarf malzemeleri de raporda görünür; depoda
  // bekleyen ama henüz bir işe bağlanmamış miktarı buradan okunur.
  const UNASSIGNED_LABEL = 'Bağlantısız';

  const ensure = (label) => {
    const key = label || UNASSIGNED_LABEL;
    if (!map.has(key)) {
      map.set(key, {
        key,
        label: key,
        assigned: 0,
        used: 0,
        remaining: 0,
        stockIds: new Set(),
        employeeIds: new Set(),
      });
    }
    return map.get(key);
  };

  movements.forEach((movement) => {
    const stock = stockById.get(movement.stockId);
    const label = labelOf(movement) || labelOf(stock);
    const row = ensure(label);
    const quantity = num(movement.quantity);

    if (isConsumptionMovement(movement)) row.used += quantity;
    else row.assigned += quantity;

    if (movement.stockId) row.stockIds.add(movement.stockId);
    if (movement.employeeId) row.employeeIds.add(movement.employeeId);
  });

  // Depoda kalan miktar hareketlerden değil, stok kartından okunur
  stocks.filter(isConsumable).forEach((stock) => {
    const row = ensure(labelOf(stock));
    row.remaining += num(stock['Kalan']);
    row.stockIds.add(stock.id);
  });

  return [...map.values()].map((row) => ({
    key: row.key,
    label: row.label,
    assigned: row.assigned,
    used: row.used,
    remaining: row.remaining,
    onHand: Math.max(0, row.assigned - row.used),
    usageRate: ratio(row.used, row.assigned),
    stockCount: row.stockIds.size,
    employeeCount: row.employeeIds.size,
  }));
};

/**
 * Malzeme bazlı özet: depoda kalan, dağıtılan, tüketilen.
 * Kalan miktar stok kartından okunur (tek doğruluk kaynağı).
 */
export const summarizeByStock = (movements = [], stocks = []) => {
  const map = new Map();

  stocks.forEach((stock) => {
    map.set(stock.id, {
      key: stock.id,
      stockId: stock.id,
      stockName: stock['Adı'] || '-',
      category: stock['Kategori'] || '',
      stockType: stock['Stok Tipi'] || '',
      customerName: stockCustomerName(stock),
      projectName: stockProjectName(stock),
      initial: num(stock['Miktar']),
      remaining: num(stock['Kalan']),
      minQuantity: num(stock['Min Miktar']),
      assigned: 0,
      used: 0,
      employeeIds: new Set(),
    });
  });

  movements.forEach((movement) => {
    const key = movement.stockId || 'unknown';
    if (!map.has(key)) {
      // Stok kartı silinmiş olabilir; hareket kaybolmasın
      map.set(key, {
        key,
        stockId: key,
        stockName: movement.stockName || 'Silinmiş stok',
        category: '',
        stockType: '',
        customerName: movement.customerName || '',
        projectName: movement.projectName || '',
        initial: 0,
        remaining: 0,
        minQuantity: 0,
        assigned: 0,
        used: 0,
        employeeIds: new Set(),
      });
    }
    const row = map.get(key);
    const quantity = num(movement.quantity);
    if (isConsumptionMovement(movement)) row.used += quantity;
    else row.assigned += quantity;
    if (movement.employeeId) row.employeeIds.add(movement.employeeId);
  });

  return [...map.values()].map((row) => ({
    ...row,
    employeeCount: row.employeeIds.size,
    onHand: Math.max(0, row.assigned - row.used),
    usageRate: ratio(row.used, row.assigned),
    isLow: row.minQuantity > 0 && row.remaining <= row.minQuantity,
  }));
};

/** Üst şeritteki toplamlar. */
export const summarizeTotals = (movements = [], stocks = []) => {
  let assigned = 0;
  let used = 0;
  movements.forEach((movement) => {
    const quantity = num(movement.quantity);
    if (isConsumptionMovement(movement)) used += quantity;
    else assigned += quantity;
  });

  const remaining = stocks.reduce((total, stock) => total + num(stock['Kalan']), 0);

  return {
    assigned,
    used,
    remaining,
    onHand: Math.max(0, assigned - used),
    usageRate: ratio(used, assigned),
  };
};

/** Sayıları raporda kısa göstermek için: 12, 12,5 */
export const formatQuantity = (value) => {
  const parsed = num(value);
  return Number.isInteger(parsed)
    ? String(parsed)
    : parsed.toLocaleString('tr-TR', { maximumFractionDigits: 2 });
};

export const formatPercent = (value) => `%${Math.round((value || 0) * 100)}`;

/** Hareketleri tarih aralığına göre süzer ('' → sınırsız). */
export const filterMovementsByDate = (movements = [], startDate = '', endDate = '') => {
  if (!startDate && !endDate) return movements;
  const start = startDate ? new Date(`${startDate}T00:00:00`).getTime() : null;
  const end = endDate ? new Date(`${endDate}T23:59:59`).getTime() : null;

  return movements.filter((movement) => {
    const time = timeOf(movement.date);
    if (!time) return false;
    if (start && time < start) return false;
    if (end && time > end) return false;
    return true;
  });
};
